import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Typography,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CircularProgress from "@mui/material/CircularProgress";
// import { AxiosResponse } from "axios";
import { errorToastMessage } from "../../utils/toast";
import http from "../../utils/http";
import Header from "./Header";
import { useTranslation } from "react-i18next";

interface StudyConsentProps {
  onConsentComplete: () => void;
}

const StudyConsent = ({ onConsentComplete }: StudyConsentProps) => {
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const studyId = localStorage.getItem("study-id");
  const { t } = useTranslation("participant-flow");

  const handleContinue = async () => {
    if (!isChecked) return;

    try {
      setIsSubmitting(true);
      // const res: AxiosResponse = 
      await http.put(
        `/participants/study/${studyId}/flags/consent`,
      );
      // toastMessage(
      //   "success",
      //   (res?.data?.message as string) || "Study consent completed",
      // );
      onConsentComplete();
    } catch (error) {
      errorToastMessage(error as Error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          py: 4,
        }}
      >
        <Card sx={{ width: "100%", maxWidth: 800 }}>
          <CardContent sx={{ "&:last-child": { pb: 4 } }}>
            <Box sx={{ textAlign: "center", mb: 1 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                  "& .MuiSvgIcon-root": { fontSize: 32 },
                }}
              >
                <AssignmentIcon />
              </Box>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem" }, mb: 2 }}
              >
                {t("CONSENT_TITLE")}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mt: 1.5, mb: 1, lineHeight: 1.6 }}
              >
                {t("CONSENT_SUBTITLE")}
              </Typography>
            </Box>
            <Box
              sx={{
                bgcolor: "grey.100",
                borderRadius: 2,
                p: 3,
                border: "1px solid",
                borderColor: "divider",
                mb: 4,
              }}
            >
              <Typography
                variant="body1"
                sx={{ lineHeight: 1.8, fontSize: "1rem" }}
              >
                {t("CONSENT_BODY")}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                p: 2.5,
                bgcolor: "background.paper",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                mb: 3,
              }}
            >
              <Checkbox
                id="consent-checkbox"
                checked={isChecked}
                onChange={(_, checked) => setIsChecked(checked)}
                sx={{ py: 0, mt: 0.25 }}
              />
              <Typography
                variant="body1"
                fontWeight={400}
                component="label"
                htmlFor="consent-checkbox"
                sx={{
                  cursor: "pointer",
                  lineHeight: 1.7,
                  fontSize: "1rem",
                  flex: 1,
                }}
                onClick={() => setIsChecked((c) => !c)}
              >
                {t("CONSENT_AGREE")}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", pt: 3 }}>
              <Button
                variant="contained"
                onClick={handleContinue}
                disabled={!isChecked || isSubmitting}
                size="large"
                sx={{
                  minWidth: 200,
                  fontWeight: 700,
                  fontSize: "1rem",
                }}
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress
                      size={20}
                      sx={{ mr: 1 }}
                      color="inherit"
                    />
                    {t("CONSENT_PROCESSING")}
                  </>
                ) : (
                  t("common:SUBMIT")
                )}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default StudyConsent;
